import * as React from 'react';
import {
  root as rootCls,
  segmentBar as segmentBarCls,
  tick as tickCls,
  label as labelCls,
  ratio as ratioCls,
} from './TrustBadge.css';
import type { TrustLevel } from '../_types';

export type TrustBadgeSize = 'sm' | 'md' | 'lg';

export interface TrustBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  level: TrustLevel;
  size?: TrustBadgeSize;
  /** Force the "1/3" ratio text on or off (defaults: off on sm, on otherwise) */
  showRatio?: boolean;
  /** Adds focus ring + keyboard accessibility. */
  interactive?: boolean;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

const LEVEL_META: Record<TrustLevel, { label: string; fill: number; ratio: string }> = {
  inLoop: { label: 'IN-LOOP', fill: 1, ratio: '1/3' },
  onLoop: { label: 'ON-LOOP', fill: 2, ratio: '2/3' },
  aboveLoop: { label: 'ABOVE-LOOP', fill: 3, ratio: '3/3' },
};

/**
 * TrustBadge — visualises an agent's trust level (in-loop / on-loop /
 * above-loop) as a 3-segment fill bar + label + optional ratio. Three
 * sizes; pair with a `<MemberChip>` or render inline next to an agent
 * name in a chat row.
 *
 * Set `interactive={true}` to expose a focus ring and `role="button"`
 * for clickable badges (e.g. opening a trust-level edit modal).
 * Otherwise renders as `role="status"` with an auto-generated
 * `aria-label` describing the level + segment count.
 */
export function TrustBadge({
  level,
  size = 'md',
  showRatio,
  interactive = false,
  'aria-label': ariaLabel,
  ref,
  className,
  ...rest
}: TrustBadgeProps) {
  const meta = LEVEL_META[level];
  const ratioShown = showRatio ?? size !== 'sm';

  const computedLabel =
    ariaLabel ??
    `Trust level: ${meta.label.replace('-', ' ').toLowerCase()}, ${meta.fill} of 3 trust segments`;

  const cls = [rootCls({ level, size, interactive }), className].filter(Boolean).join(' ');

  return (
    <span
      ref={ref}
      data-interactive={interactive ? 'true' : undefined}
      role={interactive ? 'button' : 'status'}
      tabIndex={interactive ? 0 : undefined}
      aria-label={computedLabel}
      className={cls}
      {...rest}
    >
      <span className={segmentBarCls} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={tickCls({ level, size, active: i < meta.fill })} />
        ))}
      </span>
      <span className={labelCls({ size })}>{meta.label}</span>
      {ratioShown && <span className={ratioCls({ size })}>{meta.ratio}</span>}
    </span>
  );
}
TrustBadge.displayName = 'TrustBadge';
